import { useState, useEffect, useRef } from 'react';
import { auth, db } from '../firebase';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { getUserFriendlyError } from '../utils/errorUtils';

export interface UserProfileData {
  displayName?: string;
  email?: string;
  photoURL?: string;
  originalPhotoURL?: string; // Image originale non recadrée pour permettre de recadrer à nouveau
  status?: string;
  cvFileName?: string;
  uploadedAt?: any;
  createdAt?: any;
  emailVerified?: boolean;
  targetJobTitle?: string;
  preferredLocation?: string;
  workArrangement?: string;
  experienceLevel?: string;
  salaryMin?: number;
  salaryMax?: number;
  preferences?: {
    themeVariant?: 'standard' | 'glass' | 'pastel' | 'earth' | 'sage' | 'ocean' | 'white' | 'black' | 'sunset' | 'forest' | 'midnight' | 'aurora' | 'amber' | 'emerald' | 'premium' | 'slate-blue' | 'mui-official' | string;
    iconSet?: 'none' | 'emoji' | 'material' | 'lucide' | 'lordicon';
    customThemeId?: string;
    language?: string;
  };
}

export const useUserProfile = () => {
  const [profileData, setProfileData] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const didInitDefaultThemeVariantRef = useRef(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();

          // Ensure new users get the default theme variant on first arrival (only if missing)
          // We use dot-notation to avoid overwriting other preferences.
          if (!didInitDefaultThemeVariantRef.current && !data?.preferences?.themeVariant) {
            didInitDefaultThemeVariantRef.current = true;
            updateDoc(userDocRef, { 'preferences.themeVariant': 'slate-blue' }).catch(() => {
              // Silently ignore theme initialization errors
            });
          }

          setProfileData({
            displayName: user.displayName || data.displayName || '',
            email: user.email || '',
            photoURL: user.photoURL || data.photoURL || '',
            originalPhotoURL: data.originalPhotoURL || '',
            status: data.status || 'new',
            cvFileName: data.cvFileName || '',
            uploadedAt: data.uploadedAt,
            createdAt: data.createdAt,
            emailVerified: user.emailVerified,
            targetJobTitle: data.targetJobTitle || '',
            preferredLocation: data.preferredLocation || '',
            workArrangement: data.workArrangement || '',
            experienceLevel: data.experienceLevel || '',
            salaryMin: data.salaryMin,
            salaryMax: data.salaryMax,
            preferences: data.preferences || {},
          });
        } else {
          setProfileData({
            displayName: user.displayName || '',
            email: user.email || '',
            photoURL: user.photoURL || '',
            status: 'new',
            emailVerified: user.emailVerified,
          });
        }
        setLoading(false);
      },
      () => {
        setError('Erreur de connexion. Veuillez réessayer.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const updateProfileData = async (updates: Partial<UserProfileData>) => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    try {
      // Update Firebase Auth
      const authUpdates: any = {};
      if (updates.displayName !== undefined) {
        authUpdates.displayName = updates.displayName;
      }
      if (updates.photoURL !== undefined) {
        authUpdates.photoURL = updates.photoURL;
      }

      if (Object.keys(authUpdates).length > 0) {
        await updateProfile(user, authUpdates);
      }

      // Update Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const firestoreUpdates: any = {};
      if (updates.displayName !== undefined) {
        firestoreUpdates.displayName = updates.displayName;
      }
      if (updates.photoURL !== undefined) {
        firestoreUpdates.photoURL = updates.photoURL;
      }
      if (updates.originalPhotoURL !== undefined) {
        firestoreUpdates.originalPhotoURL = updates.originalPhotoURL;
      }
      if (updates.targetJobTitle !== undefined) {
        firestoreUpdates.targetJobTitle = updates.targetJobTitle;
      }
      if (updates.workArrangement !== undefined) {
        firestoreUpdates.workArrangement = updates.workArrangement;
      }
      if (updates.experienceLevel !== undefined) {
        firestoreUpdates.experienceLevel = updates.experienceLevel;
      }
      if (updates.preferences !== undefined) {
        firestoreUpdates.preferences = updates.preferences;
      }

      if (Object.keys(firestoreUpdates).length > 0) {
        await updateDoc(userDocRef, firestoreUpdates);
      }

      return true;
    } catch (err: any) {
      throw new Error(getUserFriendlyError(err, { action: 'sauvegarde' }));
    }
  };

  return {
    profileData,
    loading,
    error,
    updateProfileData,
  };
};
